import "core-js/es/symbol/iterator"
import "./helpers/chunk-reload"
import "./vanilla"

import { createApp, defineAsyncComponent, ref } from "vue"
import Parallaxy from '@lucien144/vue3-parallaxy';
import VueLazyLoad from "vue3-lazyload"
import Paginate from "vuejs-paginate-next"
import http from "./plugins/http"
import VueObserveVisibility from "vue3-observe-visibility"
import * as Sentry from "@sentry/vue"
import { directive as onClickOutside } from "vue-on-click-outside"

import RootMixin from "./mixins/root"
import events from "./helpers/events"
/**
 * Custom plugin components
 */
const OperatorListing = defineAsyncComponent(() =>
  import(/* webpackChunkName: "operator-listings-normal" */ "./components/operator-listings/OperatorListing.vue"),
)
const OperatorListingList = defineAsyncComponent(() =>
  import(/* webpackChunkName: "operator-listings-list" */ "./components/operator-listings/OperatorListingList.vue"),
)

/**
 * Custom theme components
 */

//import HeaderLayout from "./components/HeaderLayout.vue"
//const HeaderSearch = () => import("./components/HeaderSearch.vue")

import HeroContent from "./components/HeroContent.vue"
import Ribbon from "./components/Ribbon.vue"
import ScrollToTop from "./components/ScrollToTop.vue"
import LightboxGallery from "./components/LightboxGallery.vue"
const Alert = defineAsyncComponent(() => import("./components/Alert.vue"))
const Share = defineAsyncComponent(() => import("./components/Share.vue"))

const Gallery = defineAsyncComponent(() => import("./components/Gallery.vue"))
const BannerSlider = defineAsyncComponent(() => import("./components/BannerSlider.vue"))
const Description = defineAsyncComponent(() => import("./components/Description.vue"))
const SimpleMap = defineAsyncComponent(() => import(/* webpackChunkName: "simple-map" */ "./components/SimpleMap.vue"))
const ComplexMapPopup = defineAsyncComponent(() => import("./components/ComplexMapPopup.vue"))
const Carousel = defineAsyncComponent(() => import("./components/related/Carousel.vue"))
const Featured = defineAsyncComponent(() => import("./components/related/Featured.vue"))
const Blocks = defineAsyncComponent(() => import("./components/related/Blocks.vue"))
const EventsPdf = defineAsyncComponent(() => import(/* webpackChunkName: "events-pdf" */ "./components/EventsPdf.vue"))
const Masonry = defineAsyncComponent(() => import("./components/related/Masonry.vue"))
const StarCircle = defineAsyncComponent(() => import("./components/StarCircle.vue"))
const VideoGallery = defineAsyncComponent(() => import("./components/VideoGallery.vue"))
const EmailSubscribe = defineAsyncComponent(() => import("./components/EmailSubscribe.vue"))
const EmailSubscribeBar = defineAsyncComponent(() => import("./components/EmailSubscribeBar.vue"))

const MemberImages = defineAsyncComponent(() => import("./components/members/Images.vue"))
const MemberTourList = defineAsyncComponent(() => import("./components/members/TourList.vue"))
const MemberRoomList = defineAsyncComponent(() => import("./components/members/RoomList.vue"))
const MemberDirections = defineAsyncComponent(() => import("./components/members/Directions.vue"))
const MemberAccreditations = defineAsyncComponent(() => import("./components/members/Accreditations.vue"))
const MemberAmenities = defineAsyncComponent(() => import("./components/members/Amenities.vue"))
const MemberSync = defineAsyncComponent(() => import("./components/members/Sync.vue"))

const TrailImages = defineAsyncComponent(() => import("./components/trails/Images.vue"))
const TrailPdf = defineAsyncComponent(() => import("./components/trails/TrailPdf.vue"))
const TrailMap = defineAsyncComponent(() => import("./components/trails/TrailMap.vue"))

const MemberUpdate = defineAsyncComponent(() => import("./components/MemberUpdate.vue"))
const SingleMember = defineAsyncComponent(() => import("./components/SingleMember.vue"))
const GetDirections = defineAsyncComponent(() => import("./components/GetDirections.vue"))
const MembersMap = defineAsyncComponent(() => import("./components/MembersMap.vue"))
const Logo = defineAsyncComponent(() => import("./components/Logo.vue"))
const CloseIcn = defineAsyncComponent(() => import("./components/CloseIcn.vue"))
const SearchModel = defineAsyncComponent(() => import("./components/SearchModel.vue"))
const SearchModelDesktop = defineAsyncComponent(() => import("./components/SearchModelDesktop.vue"))
const Community = defineAsyncComponent(() => import(/* webpackChunkName: "community" */ "./components/Community.vue"))
const SearchResults = defineAsyncComponent(() => import(/* webpackChunkName: "search" */ "./components/SearchResults.vue"))
const ItineraryAccordion = defineAsyncComponent(() =>
  import(/* webpackChunkName: "itinerary-accordion" */ "./components/itineraries/ItineraryAccordion.vue"),
)
const ItineraryBlocks = defineAsyncComponent(() => import(/* webpackChunkName: "itinerary-blocks" */ "./components/itineraries/ItineraryBlocks.vue"))
const VideoCarousel = defineAsyncComponent(() => import(/* webpackChunkName: "video-carousel" */ "./components/VideoCarousel.vue"))

// Cloudflare's bot detection injects <script>(function(){function c(){var b=a.contentDocument||(a.contentWindow&&a.contentWindow.document);...
// into the page, and reading its own iframe's document can throw. Only true with positive evidence it came from that
// snippet: thrown inline in this page, in c(), at the column where the snippet on this page reads contentWindow.document.
// Anything uncertain is kept
const CLOUDFLARE_FRAME_ACCESS = "a.contentWindow.document"

const isCloudflareChallengeError = (exception, frame) => {
  if (!exception?.value?.includes("Blocked a frame with origin") || frame?.function !== "c") {
    return false
  }

  if (!/^https?:\/\//.test(frame.filename ?? "")) {
    return false
  }

  let frameUrl
  try {
    frameUrl = new URL(frame.filename)
  } catch (e) {
    return false
  }

  if (frameUrl.origin !== window.location.origin || frameUrl.pathname !== window.location.pathname) {
    return false
  }

  const snippet = Array.from(document.scripts)
    .map((script) => script.text)
    .find((text) => text.startsWith("(function(){function c(){") && text.includes("/cdn-cgi/challenge-platform/"))

  if (!snippet || !snippet.includes(CLOUDFLARE_FRAME_ACCESS)) {
    return false
  }

  // the snippet sits right after its <script> tag at the start of the line, columns are 1 based.
  // Safari versions differ on whether they point at the "." or at "document" in .document
  const dotColumn = "<script>".length + snippet.indexOf(CLOUDFLARE_FRAME_ACCESS) + "a.contentWindow".length + 1

  return frame.colno === dotColumn || frame.colno === dotColumn + 1
}

if (document.querySelectorAll("#app").length > 0) {
  const app = createApp({
    components: {
      Alert,
      Share,
      HeroContent,
      //HeaderSearch,
      //HeaderLayout,
      ScrollToTop,
      Ribbon,
      OperatorListing,
      OperatorListingList,

      Description,
      Gallery,
      BannerSlider,
      LightboxGallery,
      SimpleMap,
      ComplexMapPopup,

      Blocks,
      Carousel,
      Featured,
      Masonry,

      VideoGallery,
      StarCircle,
      EventsPdf,
      EmailSubscribe,
      EmailSubscribeBar,

      Parallaxy,

      MemberImages,
      MemberTourList,
      MemberRoomList,
      MemberDirections,
      MemberAccreditations,
      MemberAmenities,
      MemberSync,

      TrailImages,
      TrailPdf,
      TrailMap,

      ItineraryAccordion,
      ItineraryBlocks,

      MemberUpdate,
      SingleMember,
      GetDirections,
      MembersMap,
      Logo,
      CloseIcn,
      SearchModel,
      SearchModelDesktop,
      Community,

      SearchResults,
      VideoCarousel,
    },
    mixins: [RootMixin],
    data: () => ({
      loading: false,
    }),
  })
    .use(VueLazyLoad)
    .use(VueObserveVisibility)
    .component("paginate", Paginate)
    .directive("on-click-outside", onClickOutside)
    .directive("fixed-width-child", {
      // When the bound element is inserted into the DOM...
      mounted(el) {
        el.children[0].style.width = `${el.clientWidth}px`
      },
    })

  app.config.globalProperties.$http = http
  app.config.globalProperties.$events = events
  app.config.globalProperties.$settings = window.storage.settings

  if (window.river?.sentry) {
    Sentry.init({
      app,
      dsn: window.river.sentry,
      // Setting this option to true will send default PII data to Sentry.
      // For example, automatic IP address collection on events
      integrations: [Sentry.replayIntegration(), Sentry.browserTracingIntegration()],
      // Session Replay
      replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
      replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.

      // Capture 10% of transactions for performance monitoring
      // Adjust this value in production (0.1 = 10%, 1.0 = 100%)
      tracesSampleRate: 0.1,

      // Setting this option to true will send default PII data to Sentry.
      // For example, automatic IP address collection on events
      sendDefaultPii: true,

      // Track Vue component performance
      trackComponents: true,

      // Errors thrown by third party scripts, in-app browsers and crawlers that we can't fix
      denyUrls: [
        // Zoho SalesIQ chat widget
        /zohocdn\.com/,
        /salesiq\.zoho/,
        // Microsoft Clarity session recording
        /scripts\.clarity\.ms/,
      ],
      ignoreErrors: [
        // Android and iOS in-app browser bridges
        "Java object is gone",
        "Java exception was raised during method invocation",
        "WKWebView API client did not respond to this postMessage",
        "window.webkit.messageHandlers",
        "_pcmBridgeCallbackHandler",
        // Analytics and ad requests blocked by extensions (Sentry appends the host to fetch errors)
        /\(([\w-]+\.)*(analytics\.google\.com|google-analytics\.com|doubleclick\.net)\)$/,
        // Outlook Safe Links scanner
        "Object Not Found Matching Id",
        // browser extensions
        "Invalid call to runtime.sendMessage(). Tab not found.",
      ],
      beforeSend(event) {
        // Email link scanners render our pages on other domains (Campaign Monitor's createsend1.com), where our script chunks 404
        if (!/(^|\.)margaretriver\.com$/.test(window.location.hostname)) {
          return null
        }

        const exception = event.exception?.values?.[0]
        const frames = exception?.stacktrace?.frames || []

        // Microsoft's crawler injects "tmsa" functions that throw inside our page's code
        if (frames.some((frame) => frame.function?.startsWith("tmsa"))) {
          return null
        }

        // Headless "obscura" scraper runs our bundle outside a real browser
        if (frames.some((frame) => frame.filename?.startsWith("<obscura:"))) {
          return null
        }

        if (isCloudflareChallengeError(exception, frames[frames.length - 1])) {
          return null
        }

        return event
      },
    })
  }

  app.mount("#app")
}
